import DeleteOutlineIcons from '@/icons/DeleteOutlineIcons'
import { PRICING_MODELS } from '@/modules/Contract/constants'
import { Contract } from '@/modules/Contract/types'
import React from 'react'
import { Path, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Checkbox, GridHookForm, IconButton } from 'vnetwork-ui'
import VMInstanceCustomize from './VMInstanceCustomize'

interface VMInstanceItemProps {
    servicePriceModel: string
    isPOC?: boolean
    type: 'vm-instance' | 'vm-lb' | 'vm-other'
    data: any
    onEdit?: (updatedData: any) => void
    onDelete?: () => void
    configOptions?: Array<{ name: string; title?: string; price?: number }>
    vmName?: string
    vmPackageType?: 'predefined' | 'custom'
}

const VMInstanceItem: React.FC<VMInstanceItemProps> = ({
    servicePriceModel,
    isPOC = false,
    type,
    data,
    vmName,
    configOptions = [],
    onDelete,
    vmPackageType = 'predefined',
}) => {
    const { t } = useTranslation()
    const formControl = useFormContext()
    // Just get the initial values from data for defaultValues
    const initialValues = {
        name: data?.name || '',
        configuration: data?.configuration || data?.name || '',
        quantity: data?.commitment_value || data?.quantity || 1,
        price: data?.commitment_price_per_unit || data?.price || 0,
        total: data?.commitment_price_total || 0,
        type: data?.type || type,
        // Custom quota fields for VM instances
        customName: data?.customName || '',
        vcpu: data?.vcpu || 0,
        ram: data?.ram || 0,
        storage: data?.storage || 0,
    }

    // Filter options based on type - if no matches, show all options
    const getFilteredOptions = () => {
        if (!configOptions || configOptions.length === 0) {
            return []
        }

        const filtered = configOptions?.filter((option) => {
            const optionName = option.title?.toLowerCase() || ''

            switch (type) {
                case 'vm-instance':
                    return optionName.includes('vm-instance')
                case 'vm-lb':
                    return optionName.includes('vm-lb')
                case 'vm-other':
                    // For other services, exclude vm and lb options
                    return (
                        !optionName.includes('vm-instance') &&
                        !optionName.includes('vm-lb')
                    )
                default:
                    return false
            }
        })

        // If no filtered results, return empty array instead of all options
        if (filtered.length === 0) {
            console.log(
                'No filtered options found for type:',
                type,
                'Available options:',
                configOptions.map((o) => o.name)
            )
            return [] // Don't fallback to all options
        }

        return filtered
    }

    const filteredOptions = getFilteredOptions()

    // Render different forms based on vmPackageType for vm-instance
    const renderForm = () => {
        if (type === 'vm-instance' && vmPackageType === 'custom') {
            // Custom quota form for VM instances
            return <VMInstanceCustomize vmName={vmName} data={data} />
        } else {
            // Predefined form (original behavior for all types, including vm-instance in predefined mode)
            return (
                <GridHookForm
                    formControl={formControl}
                    subForm={true}
                    actions={[]}
                    data={[
                        {
                            formItemProps: {
                                name: `${vmName}.[${data.vmIndex}].name` as Path<Contract>,
                                type: 'select',
                                options: filteredOptions.map((opt) => ({
                                    label: opt.name,
                                    value: opt.name,
                                })),
                                allowClear: false,
                                label: t('common.select_config'),
                                placeholder: t('common.select_config'),
                                defaultValue: initialValues.configuration,
                                onChangeAfterHookForm: (value: any) => {
                                    const selectedConfig = filteredOptions.find(
                                        (opt) => opt.name === value
                                    )
                                    const price = selectedConfig?.price || 0
                                    formControl.setValue(
                                        `${vmName}.[${data.vmIndex}].commitment_price_per_unit`,
                                        price
                                    )
                                },
                            },
                            className:
                                servicePriceModel !== PRICING_MODELS.Bundle
                                    ? 'col-span-2'
                                    : 'col-span-1',
                        },
                        {
                            formItemProps: {
                                name: `${vmName}.[${data.vmIndex}].commitment_value` as Path<Contract>,
                                type: 'number',
                                label: t(
                                    isPOC
                                        ? 'poc_commitment_value'
                                        : type !== 'vm-other'
                                          ? 'vm_package_qty'
                                          : 'vm_commitment_value'
                                ),
                                placeholder: t(
                                    isPOC
                                        ? 'poc_commitment_value_placeholder'
                                        : type !== 'vm-other'
                                          ? 'vm_package_qty'
                                          : 'vm_commitment_value'
                                ),
                            },
                            className: 'col-span-1',
                        },
                        {
                            formItemProps: {
                                name: `${vmName}.[${data.vmIndex}].commitment_price_per_unit` as Path<Contract>,
                                type: 'number',
                                label: t('commitment_price_per_unit'),
                                placeholder: t('commitment_price_per_unit'),
                            },
                            className: isPOC
                                ? 'hidden'
                                : servicePriceModel === PRICING_MODELS.Bundle
                                  ? 'col-span-1'
                                  : 'hidden',
                        },
                        {
                            renderEditComponent: () => (
                                <div className="flex justify-end items-center gap-2 h-full">
                                    {!isPOC && (
                                        <div className="flex-1 border rounded-sm px-4 py-auto h-full flex items-center">
                                            <Checkbox
                                                label={t(
                                                    'contract.monthly_statement'
                                                )}
                                                checked={data.statementable}
                                                onChange={(e) => {
                                                    formControl.setValue(
                                                        `${vmName}.[${data.vmIndex}].statementable`,
                                                        e.target.checked
                                                    )
                                                }}
                                            />
                                        </div>
                                    )}
                                    {onDelete && (
                                        <IconButton
                                            onClick={onDelete}
                                            size="small"
                                            className="text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                                        >
                                            <DeleteOutlineIcons className="w-4 h-4" />
                                        </IconButton>
                                    )}
                                </div>
                            ),
                            className: isPOC ? 'col-span-2' : 'col-span-1',
                        },
                    ]}
                />
            )
        }
    }

    return <>{renderForm()}</>
}

export default VMInstanceItem
